import { UserInfo } from "@/features/authentication/context/auth/auth.types";
import { apiLegacy } from "@/services/api-legacy";
import { validateAuthentication } from "@/utils/auth-middleware";

const analytics = AnalyticsBrowser.load({ writeKey: SEGMENT_KEY });

import {
  DEFAULT_PROPERTIES,
  EVENTS,
  EVENTS_PROPERTIES,
  PRODUCT_TYPE,
} from "../constants/events";
import { SEGMENT_KEY } from "../constants/key";

import { AnalyticsBrowser, EventProperties } from "@segment/analytics-next";

async function getUserInfoFromCookies() {
  const isExternalRoute = window.location.pathname.includes("/external/");
  const authResult = validateAuthentication(isExternalRoute);

  if (!authResult.isAuthenticated || authResult.isExternal) {
    return null;
  }

  try {
    const { data: userInfo } = (await apiLegacy.get(
      "/v0/users/me?fl=profile,plan",
    )) as { data: UserInfo };

    return userInfo;
  } catch (error) {
    console.error("Failed to get user info from cookies:", error);
    return null;
  }
}

export const sendTrackDataServer = async <T extends keyof typeof EVENTS>({
  data,
  url,
  eventName,
  productType,
}: {
  eventName: T;
  data: EVENTS_PROPERTIES[T];
  url: string;
  productType: PRODUCT_TYPE;
}) => {
  const userInfo = await getUserInfoFromCookies();

  if (userInfo?.user?.id) {
    sendUserIdentifyData(userInfo);
  }

  const defaultData: DEFAULT_PROPERTIES = {
    url_page: url,
    product_type: productType,
  };

  const trackData: DEFAULT_PROPERTIES & EVENTS_PROPERTIES[T] = {
    ...defaultData,
    ...data,
  };

  sendTrackData({ eventName: EVENTS[eventName], data: trackData });
};

export const sendTrackData = async <
  T = EVENTS_PROPERTIES[keyof typeof EVENTS],
>({
  eventName,
  data,
}: {
  eventName: string;
  data: T;
}) => {
  if (!analytics) {
    console.error("Analytics instance is not defined.");
    return;
  }

  try {
    await analytics.track(eventName, data as EventProperties);
  } catch (error) {
    console.error(`Failed to send track data for event: ${eventName}`, error);
  }
};

/** Identify for public (unauthenticated) routes — attaches the email as a
 * trait on the visitor's anonymous identity so downstream tools (Amplitude)
 * can resolve identity on every event, including ones without an email
 * property. No userId on purpose: the anonymous device id stays the identity;
 * we only enrich it. */
export const sendAnonymousIdentifyData = async (email: string) => {
  if (!analytics || !email) return;
  try {
    await analytics.identify({ email });
  } catch (error) {
    console.error("Failed to identify anonymous user:", error);
  }
};

export const sendUserIdentifyData = async (userInfo: UserInfo) => {
  if (!analytics) return;
  try {
    await analytics.identify(`${userInfo.user?.id}`, {
      email: userInfo?.profile?.email,
      is_plg: userInfo?.user?.is_plg,
      league_id: userInfo?.league?.id,
      league_name: userInfo?.league?.name,
      plan_id: userInfo?.plan?.id,
      plan_name: userInfo?.plan?.name,
    });
  } catch (error) {
    console.error("Failed to identify user:", error);
  }
};
